import { useMemo } from 'react';
import { useApiCall } from './useApi';
import { demoVisibleAlerts, isDemoPolishEnabled, visibleDemoClients, visibleDemoMembers } from '../utils/mspPolish';

function toArray(data) {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    for (const key of ['items', 'results', 'data', 'clients', 'alerts', 'members', 'keys']) {
      if (Array.isArray(data[key])) return data[key];
    }
  }
  return [];
}

export function useMspClients() {
  const { data, loading, error, refetch } = useApiCall('/clients');
  return { data: visibleDemoClients(toArray(data)), loading, error, refetch };
}

const ALERT_SEV = { 0: 'low', 1: 'medium', 2: 'high', 3: 'critical' };

export function useMspAlerts() {
  const { data, loading, error } = useApiCall('/alerts');
  const alerts = useMemo(() => demoVisibleAlerts(toArray(data).map(a => {
    if (!a) return null;
    const rawSev = a.severity ?? a.sev;
    const severity = typeof rawSev === 'number'
      ? (ALERT_SEV[rawSev] ?? 'info')
      : (typeof rawSev === 'string' ? rawSev.toLowerCase() : 'info');
    return {
      id: a.alertId ?? a.id ?? '',
      severity,
      msg: a.message ?? a.msg ?? '',
      client: a.clientName ?? a.client ?? '',
      time: a.createdAt ?? a.time ?? null,
      read: a.readAt != null || a.read === true,
      findingId: a.relatedFindingId ?? null,
    };
  }).filter(Boolean)), [data]);
  return { data: alerts, loading, error };
}

export function useMspTeam() {
  const { data, loading, error, refetch } = useApiCall('/team');
  return { data: visibleDemoMembers(toArray(data)), loading, error, refetch };
}

export function useMspApiKeys() {
  const { data, loading, error, refetch } = useApiCall('/msp/api-keys');
  return { data: toArray(data), loading, error, refetch };
}

export function useMspSummary() {
  const { data, loading, error } = useApiCall('/msp/summary');
  return { data, loading, error };
}

export function useMspScanQueue() {
  const { data, loading, error, refetch } = useApiCall('/msp/scan-queue');
  const running = toArray(data?.running);
  const queued = toArray(data?.queued);
  const schedules = toArray(data?.schedules).filter(s => {
    const clientName = String(s?.clientName || '').trim();
    if (!clientName || /^[0-9a-f]{8}-/i.test(clientName)) return false;
    return !isDemoPolishEnabled() || clientName.toLowerCase() === 'demo environment';
  });
  return { running, queued, schedules, loading, error, refetch };
}
